import { PcmDecoder } from "./pcm.js";

/** Gateway route that proxies the voice service's `POST /tts` with the user's voice. */
export const TTS_PATH = "/api/v1/voice/tts";

export class TtsError extends Error {
  override readonly name = "TtsError";
}

/** The parts of the Web Audio API the player uses (injectable for tests). */
export interface PlayerAudioContext {
  readonly currentTime: number;
  readonly destination: AudioNode;
  readonly state: string;
  createBuffer(channels: number, length: number, sampleRate: number): AudioBuffer;
  createBufferSource(): AudioBufferSourceNode;
  resume(): Promise<void>;
  close(): Promise<void>;
}

/**
 * Plays float sample chunks back to back without gaps: every chunk is scheduled at the end of the
 * previous one. `stop()` cancels everything (barge-in).
 */
export class PcmPlayer {
  private ctx: PlayerAudioContext | null = null;
  private nextStart = 0;
  private sources = new Set<AudioBufferSourceNode>();
  private idleWaiters: (() => void)[] = [];

  constructor(private readonly makeContext: () => PlayerAudioContext) {}

  get playing(): boolean {
    return this.sources.size > 0;
  }

  enqueue(samples: Float32Array, sampleRate: number): void {
    if (samples.length === 0) return;
    const ctx = (this.ctx ??= this.makeContext());
    if (ctx.state === "suspended") void ctx.resume().catch(() => undefined);
    const buffer = ctx.createBuffer(1, samples.length, sampleRate);
    buffer.getChannelData(0).set(samples);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    const at = Math.max(ctx.currentTime + 0.02, this.nextStart);
    this.nextStart = at + samples.length / sampleRate;
    this.sources.add(source);
    source.onended = () => {
      this.sources.delete(source);
      if (this.sources.size === 0) this.wakeIdle();
    };
    source.start(at);
  }

  /** Resolves when everything queued so far has played (or was stopped). */
  idle(): Promise<void> {
    if (this.sources.size === 0) return Promise.resolve();
    return new Promise((resolve) => this.idleWaiters.push(resolve));
  }

  stop(): void {
    for (const s of this.sources) {
      s.onended = null;
      try {
        s.stop();
      } catch {
        // Not started yet or already stopped.
      }
    }
    this.sources.clear();
    this.nextStart = 0;
    this.wakeIdle();
  }

  async close(): Promise<void> {
    this.stop();
    await this.ctx?.close().catch(() => undefined);
    this.ctx = null;
  }

  private wakeIdle(): void {
    const waiters = this.idleWaiters;
    this.idleWaiters = [];
    for (const w of waiters) w();
  }
}

export interface SpeakServerOptions {
  text: string;
  token: string | null;
  voiceId?: string | null;
  language?: "tr" | "en";
  player: PcmPlayer;
  fetch?: typeof fetch;
  baseUrl?: string;
  signal?: AbortSignal;
}

/**
 * Streams `POST /api/v1/voice/tts` (raw `pcm_s16le` mono, rate in `x-sample-rate`) into the
 * player as chunks arrive, so audio starts after the first sentence. Resolves when playback ends.
 */
export async function speakWithServer(opts: SpeakServerOptions): Promise<void> {
  const doFetch = opts.fetch ?? globalThis.fetch.bind(globalThis);
  let res: Response;
  try {
    res = await doFetch(`${opts.baseUrl ?? ""}${TTS_PATH}`, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        ...(opts.token ? { authorization: `Bearer ${opts.token}` } : {}),
      },
      body: JSON.stringify({
        text: opts.text,
        ...(opts.voiceId ? { voiceId: opts.voiceId } : {}),
        ...(opts.language ? { language: opts.language } : {}),
      }),
      ...(opts.signal ? { signal: opts.signal } : {}),
    });
  } catch (err) {
    if (opts.signal?.aborted) return;
    throw new TtsError(err instanceof Error ? err.message : "network error");
  }
  if (!res.ok || !res.body) throw new TtsError(`speech service answered ${res.status}`);
  const rate = Number(res.headers.get("x-sample-rate"));
  const sampleRate = Number.isFinite(rate) && rate > 0 ? rate : 22050;
  const decoder = new PcmDecoder();
  const reader = res.body.getReader();
  try {
    for (;;) {
      if (opts.signal?.aborted) {
        await reader.cancel().catch(() => undefined);
        return;
      }
      const { done, value } = await reader.read();
      if (done) break;
      if (value?.length) opts.player.enqueue(decoder.push(value), sampleRate);
    }
  } catch (err) {
    if (opts.signal?.aborted) return;
    throw new TtsError(err instanceof Error ? err.message : "stream failed");
  } finally {
    reader.releaseLock();
  }
  if (!opts.signal?.aborted) await opts.player.idle();
}
